import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, CheckCircle2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { TREATMENTS } from "@/data/static";
import { toast } from "sonner";

const initial = { name:"", age:"", gender:"", phone:"", email:"", problem:"", preferred_date:"", preferred_time:"", report_url:"" };

export default function Appointment() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/public", fd, { headers:{"Content-Type":"multipart/form-data"} });
      set("report_url", data.url);
      toast.success("Report uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/appointments", { ...form, age: parseInt(form.age || "0") });
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error("Could not book appointment");
    } finally { setLoading(false); }
  };

  if (done) return (
    <main className="pt-32 pb-16 container-x text-center" data-testid="appointment-success">
      <CheckCircle2 className="w-16 h-16 text-teal mx-auto"/>
      <h1 className="font-heading text-4xl mt-5">Appointment Requested</h1>
      <p className="text-slate-600 mt-3 max-w-md mx-auto">Our team will call you within 2 hours to confirm. For urgent cases, please call us directly.</p>
      <Button onClick={()=>setDone(false)} className="rounded-full mt-7 bg-primary hover:bg-primary-dark">Book Another</Button>
    </main>
  );

  return (
    <main className="pt-32 pb-16" data-testid="appointment-page">
      <div className="container-x grid lg:grid-cols-2 gap-12">
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}}>
          <span className="overline">Appointment</span>
          <h1 className="font-heading text-5xl sm:text-6xl tracking-tight font-light mt-3">Book a <em className="italic font-medium text-primary">consultation</em></h1>
          <p className="mt-4 text-slate-600 max-w-md">Share your concern and our team will reach out within 2 hours to schedule your visit with Dr. Kumar.</p>
          <div className="mt-10 space-y-4">
            {["Direct consultation with Dr. Kumar","Cashless insurance support","Free follow-up after surgery"].map((t,i)=>(
              <div key={i} className="flex gap-3"><Calendar className="w-5 h-5 text-primary mt-0.5"/><div className="text-slate-700">{t}</div></div>
            ))}
          </div>
        </motion.div>
        <form onSubmit={submit} className="glass rounded-3xl p-8 shadow-glass space-y-5" data-testid="appointment-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Patient Name</Label><Input required value={form.name} onChange={e=>set("name",e.target.value)} data-testid="appt-name" className="mt-2"/></div>
            <div><Label>Age</Label><Input required type="number" value={form.age} onChange={e=>set("age",e.target.value)} data-testid="appt-age" className="mt-2"/></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={v=>set("gender",v)}>
                <SelectTrigger className="mt-2" data-testid="appt-gender"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Phone</Label><Input required value={form.phone} onChange={e=>set("phone",e.target.value)} data-testid="appt-phone" className="mt-2"/></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>set("email",e.target.value)} data-testid="appt-email" className="mt-2"/></div>
          <div>
            <Label>Problem / Treatment Interest</Label>
            <Select value={form.problem} onValueChange={v=>set("problem",v)}>
              <SelectTrigger className="mt-2" data-testid="appt-problem"><SelectValue placeholder="Select condition"/></SelectTrigger>
              <SelectContent>
                {TREATMENTS.map(t=>(<SelectItem key={t.slug} value={t.title}>{t.title}</SelectItem>))}
                <SelectItem value="Other / General Consultation">Other / General Consultation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Preferred Date</Label><Input required type="date" value={form.preferred_date} onChange={e=>set("preferred_date",e.target.value)} data-testid="appt-date" className="mt-2"/></div>
            <div><Label>Preferred Time</Label><Input required type="time" value={form.preferred_time} onChange={e=>set("preferred_time",e.target.value)} data-testid="appt-time" className="mt-2"/></div>
          </div>
          <div>
            <Label>Medical Report (optional)</Label>
            <label className="mt-2 flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-slate-300 bg-white/70 p-4 hover:border-primary transition-colors">
              <Upload className="w-5 h-5 text-primary"/>
              <span className="text-sm text-slate-600">{uploading?"Uploading…":(form.report_url?"Uploaded ✓ — replace":"Click to upload PDF or image")}</span>
              <input type="file" accept="application/pdf,image/*" onChange={upload} className="hidden" data-testid="appt-file"/>
            </label>
          </div>
          <Button type="submit" disabled={loading} className="rounded-full bg-primary hover:bg-primary-dark px-7 py-6 w-full" data-testid="appt-submit">
            {loading?"Booking…":"Book Appointment"}
          </Button>
        </form>
      </div>
    </main>
  );
}
